import { useEffect, useState, useCallback } from "react";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Banknote, Inbox, CheckCircle2, AlertCircle, CopyX, DollarSign, Mail, RefreshCw, Send, X, Pencil, Trash2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const STATUS_STYLE = {
  "Posted Automatically": "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10",
  "Needs Review": "bg-amber-50 text-amber-700 dark:bg-amber-500/10",
  "Partial Payment": "bg-blue-50 text-blue-700 dark:bg-blue-500/10",
  "Overpayment": "bg-violet-50 text-violet-700 dark:bg-violet-500/10",
  "Duplicate": "bg-slate-100 text-slate-600 dark:bg-slate-500/10",
  "Ignored": "bg-muted text-muted-foreground",
};

const FILTERS = ["All", "Needs Review", "Posted Automatically", "Partial Payment", "Overpayment", "Duplicate", "Ignored"];

function StatCard({ icon: Icon, label, value, accent, i, onClick }) {
  return (
    <div onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`rounded-2xl border border-border bg-card p-4 kpi-enter transition-all ${onClick ? "cursor-pointer hover:border-primary/40 hover:shadow-sm" : ""}`} style={{ animationDelay: `${i * 40}ms` }} data-testid={`zelle-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${accent}`}><Icon className="w-4.5 h-4.5" strokeWidth={1.75} /></div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="font-heading text-xl font-bold tracking-tight mt-0.5">{value}</p>
    </div>
  );
}

export default function ZellePayments() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [filter, setFilter] = useState("Needs Review");
  const [raw, setRaw] = useState("");
  const [resolving, setResolving] = useState(null);
  const [polling, setPolling] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/zelle/payments").then((r) => setRows(r.data)).catch(() => {});
    api.get("/zelle/stats").then((r) => setStats(r.data.zelle)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/tenants").then((r) => setTenants(r.data.filter((t) => t.status === "Active"))).catch(() => {});
  }, [load]);

  const ingest = async () => {
    if (!raw.trim()) { toast.error("Paste a Zelle email first"); return; }
    try {
      const { data } = await api.post("/zelle/ingest", { raw_text: raw });
      toast.success(`Processed — ${data.status}${data.matched_tenant_name ? " → " + data.matched_tenant_name : ""}`);
      setRaw("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const poll = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/gmail/poll");
      if (data.reason) toast.info(data.reason);
      else toast.success(`Polled ${data.polled} emails, ingested ${data.ingested}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPolling(false); }
  };

  const visible = filter === "All" ? rows : rows.filter((r) => r.status === filter);

  const removeRow = async (r) => {
    if (!window.confirm(`Delete this Zelle payment${r.sender_name ? ` from ${r.sender_name}` : ""} (${money(r.amount)})?${r.payment_record_id ? " Its posted rent entry will also be removed." : ""} This cannot be undone.`)) return;
    try {
      await api.delete(`/zelle/payments/${r.id}`);
      toast.success("Zelle payment deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="page-zelle">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Automated Rent</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Banknote className="w-7 h-7 text-primary" /> Zelle Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Auto-posted Zelle rent payments and the review queue for unmatched ones.</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button onClick={() => setDupOpen(true)} data-testid="find-duplicates-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card text-sm font-medium hover:bg-muted transition-colors">
              <CopyX className="w-4 h-4" /> Find Duplicates
            </button>
            <button onClick={poll} disabled={polling} data-testid="zelle-poll-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${polling ? "animate-spin" : ""}`} /> Check Gmail Now
            </button>
          </div>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard i={0} icon={Inbox} label="Received Today" value={stats.received_today} accent="bg-blue-50 text-blue-600 dark:bg-blue-500/10" onClick={() => setFilter("All")} />
          <StatCard i={1} icon={CheckCircle2} label="Posted Auto" value={stats.posted_automatically} accent="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10" onClick={() => setFilter("Posted Automatically")} />
          <StatCard i={2} icon={AlertCircle} label="Needs Review" value={stats.needs_review} accent="bg-amber-50 text-amber-600 dark:bg-amber-500/10" onClick={() => setFilter("Needs Review")} />
          <StatCard i={3} icon={CopyX} label="Duplicates Blocked" value={stats.duplicates_blocked} accent="bg-slate-100 text-slate-600 dark:bg-slate-500/10" onClick={() => setFilter("Duplicate")} />
          <StatCard i={4} icon={DollarSign} label="Zelle This Month" value={money(stats.total_zelle_month)} accent="bg-violet-50 text-violet-600 dark:bg-violet-500/10" onClick={() => setFilter("All")} />
        </div>
      )}

      {canWrite && (
        <section className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold">Paste / Forward a Zelle Email</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Paste a Zelle payment confirmation email. The system extracts the amount, sender, and confirmation number, then matches it to a tenant — auto-posting clear matches and queuing the rest for review.</p>
          <textarea
            value={raw} onChange={(e) => setRaw(e.target.value)} rows={4}
            data-testid="zelle-ingest-input"
            placeholder={'e.g. "John Smith sent you $1,500.00 with Zelle. Confirmation# AB12CD34. Memo: April rent — Unit 2B"'}
            className="w-full rounded-xl border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="flex justify-end mt-3">
            <button onClick={ingest} data-testid="zelle-ingest-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
              <Send className="w-4 h-4" /> Process Payment
            </button>
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`zelle-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
            {f}{f !== "All" ? ` (${rows.filter((r) => r.status === f).length})` : ` (${rows.length})`}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Date</th>
                <th className="text-left font-semibold px-4 py-3">Sender</th>
                <th className="text-right font-semibold px-4 py-3">Amount</th>
                <th className="text-left font-semibold px-4 py-3">Matched Tenant</th>
                <th className="text-left font-semibold px-4 py-3">Confidence</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-left font-semibold px-4 py-3">Source</th>
                {canWrite && <th className="text-right font-semibold px-4 py-3">Action</th>}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={canWrite ? 8 : 7} className="px-4 py-10 text-center text-muted-foreground">No payments in this view.</td></tr>
              )}
              {visible.map((r) => (
                <tr key={r.id} onClick={canWrite ? () => setResolving(r) : undefined} className={`border-t border-border hover:bg-muted/40 transition-colors ${canWrite ? "cursor-pointer" : ""}`} data-testid={`zelle-row-${r.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{String(r.date_received).slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.sender_name || "—"}</p>
                    {r.memo && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{r.memo}</p>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{money(r.amount)}</td>
                  <td className="px-4 py-3">{r.matched_tenant_name || (r.suggested_tenant_name ? <span className="text-muted-foreground italic">{r.suggested_tenant_name}?</span> : "—")}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full ${r.match_confidence >= 80 ? "bg-emerald-500" : r.match_confidence >= 40 ? "bg-amber-500" : "bg-red-400"}`} style={{ width: `${r.match_confidence}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground">{r.match_confidence}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[r.status] || "bg-muted"}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{r.source}</td>
                  {canWrite && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {r.status === "Needs Review" ? (
                          <button onClick={(e) => { e.stopPropagation(); setResolving(r); }} data-testid={`zelle-review-${r.id}`} className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors">Review</button>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setResolving(r); }} data-testid={`zelle-edit-${r.id}`} title="Edit / re-assign" className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"><Pencil className="w-4 h-4" /></button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); removeRow(r); }} data-testid={`zelle-delete-${r.id}`} title="Delete this payment" className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ResolveDialog item={resolving} tenants={tenants} onClose={() => setResolving(null)} onDone={() => { setResolving(null); load(); }} />
      <DuplicatesDialog open={dupOpen} onClose={() => setDupOpen(false)} onDone={() => { setDupOpen(false); load(); }} />
    </div>
  );
}

function DuplicatesDialog({ open, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [keep, setKeep] = useState({});      // clusterIdx -> id to keep
  const [del, setDel] = useState({});         // paymentId -> bool (marked for deletion)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/zelle/duplicates").then((r) => {
      const cl = r.data.clusters || [];
      setClusters(cl);
      const k = {}, d = {};
      cl.forEach((c, i) => {
        k[i] = c.suggested_keep_id;
        c.payments.forEach((p) => { d[p.id] = p.id !== c.suggested_keep_id; });
      });
      setKeep(k); setDel(d);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open]);

  const setKeepFor = (ci, id, payments) => {
    setKeep((s) => ({ ...s, [ci]: id }));
    // The kept record is never deleted; others in the cluster default to delete.
    setDel((s) => {
      const next = { ...s };
      payments.forEach((p) => { next[p.id] = p.id !== id; });
      return next;
    });
  };

  const selectedIds = Object.entries(del).filter(([, v]) => v).map(([id]) => id);

  const bulkDelete = async () => {
    if (selectedIds.length === 0) { toast.error("Nothing selected to delete"); return; }
    if (!window.confirm(`Delete ${selectedIds.length} duplicate payment(s)? The record(s) you marked "Keep" will NOT be deleted. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/zelle/payments/bulk-delete", { ids: selectedIds });
      toast.success(`Deleted ${data.deleted} duplicate(s)${data.removed_payments ? `, ${data.removed_payments} ledger entr${data.removed_payments === 1 ? "y" : "ies"} removed` : ""}.`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CopyX className="w-5 h-5 text-primary" /> Review Duplicate Payments</DialogTitle>
          <DialogDescription>Suspected duplicates share the same amount, date and sender. Choose which record to <b>keep</b>; the rest are marked for deletion. Nothing is deleted until you confirm.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Scanning payments…</div>
        ) : clusters.length === 0 ? (
          <div className="py-12 text-center" data-testid="no-duplicates">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
            <p className="text-sm text-muted-foreground">No suspected duplicates found. Your Zelle records look clean.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {clusters.map((c, ci) => (
              <div key={ci} className="rounded-xl border border-border overflow-hidden" data-testid={`dup-cluster-${ci}`}>
                <div className="bg-muted/50 px-4 py-2 text-sm font-medium flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-heading font-bold">{money(c.amount)}</span>
                  <span className="text-muted-foreground">·</span>
                  <span>{c.date}</span>
                  <span className="text-muted-foreground">·</span>
                  <span>{c.sender}</span>
                  <span className="ml-auto text-xs text-amber-600 font-semibold">{c.count} matching records</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
                  {c.payments.map((p) => {
                    const isKeep = keep[ci] === p.id;
                    return (
                      <div key={p.id} data-testid={`dup-payment-${p.id}`}
                        className={`rounded-lg border p-3 text-xs transition-all ${isKeep ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-500/5" : del[p.id] ? "border-destructive/50 bg-destructive/5" : "border-border"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[p.status] || "bg-muted"}`}>{p.status}</span>
                          {p.payment_record_id && <span className="text-[10px] text-emerald-600 font-semibold">POSTED</span>}
                        </div>
                        <p><span className="text-muted-foreground">Received:</span> {String(p.date_received).slice(0, 16).replace("T", " ")}</p>
                        <p><span className="text-muted-foreground">Tenant:</span> {p.matched_tenant_name || "—"}</p>
                        <p><span className="text-muted-foreground">Confirmation #:</span> {p.confirmation_number || "—"}</p>
                        <p><span className="text-muted-foreground">Source:</span> {p.source}</p>
                        {p.memo && <p className="truncate"><span className="text-muted-foreground">Memo:</span> {p.memo}</p>}
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border/60">
                          <label className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name={`keep-${ci}`} checked={isKeep} onChange={() => setKeepFor(ci, p.id, c.payments)} data-testid={`dup-keep-${p.id}`} />
                            <span className={isKeep ? "text-emerald-600 font-semibold" : ""}>Keep</span>
                          </label>
                          <label className={`inline-flex items-center gap-1.5 ${isKeep ? "opacity-40" : "cursor-pointer"}`}>
                            <input type="checkbox" checked={!!del[p.id] && !isKeep} disabled={isKeep} onChange={(e) => setDel((s) => ({ ...s, [p.id]: e.target.checked }))} data-testid={`dup-del-${p.id}`} />
                            <span className={del[p.id] && !isKeep ? "text-destructive font-semibold" : ""}>Delete</span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <div className="flex items-center justify-between w-full gap-3">
            <span className="text-sm text-muted-foreground" data-testid="dup-selected-count">{selectedIds.length} marked for deletion</span>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground px-3 py-2"><X className="w-4 h-4" /> Close</button>
              {clusters.length > 0 && (
                <button onClick={bulkDelete} disabled={busy || selectedIds.length === 0} data-testid="bulk-delete-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-destructive text-destructive-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
                  <Trash2 className="w-4 h-4" /> Delete {selectedIds.length} Duplicate{selectedIds.length === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResolveDialog({ item, tenants, onClose, onDone }) {
  const [tenantId, setTenantId] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    setTenantId(item?.suggested_tenant_id || "");
    setNote(item?.note || "");
  }, [item]);

  if (!item) return null;

  const act = async (action) => {
    try {
      await api.post(`/zelle/payments/${item.id}/resolve`, { action, tenant_id: tenantId, note });
      toast.success("Payment updated");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete this Zelle payment (${money(item.amount)})?${item.payment_record_id ? " Its posted rent entry will also be removed." : ""} This cannot be undone.`)) return;
    try {
      await api.delete(`/zelle/payments/${item.id}`);
      toast.success("Zelle payment deleted");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="zelle-resolve-dialog">
        <DialogHeader>
          <DialogTitle>Payment — {money(item.amount)} <span className="text-sm font-normal text-muted-foreground">· {item.status}</span></DialogTitle>
          <DialogDescription>Re-assign this Zelle payment to a tenant, change how it's handled, or delete it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-xl bg-muted/50 p-3 space-y-1">
            <p><span className="text-muted-foreground">Sender:</span> {item.sender_name || "Unknown"}</p>
            {item.sender_email && <p><span className="text-muted-foreground">Email:</span> {item.sender_email}</p>}
            {item.confirmation_number && <p><span className="text-muted-foreground">Confirmation:</span> {item.confirmation_number}</p>}
            {item.memo && <p><span className="text-muted-foreground">Memo:</span> {item.memo}</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assign to tenant</label>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="zelle-resolve-tenant"
              className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="">— Select tenant —</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.property_name} {t.unit}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="zelle-resolve-note"
              className="w-full mt-1 rounded-xl border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Optional note" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => act("assign")} data-testid="zelle-action-assign" className="px-3 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">Post Payment</button>
            <button onClick={() => act("partial")} data-testid="zelle-action-partial" className="px-3 py-2 rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 text-sm font-medium hover:opacity-80">Mark Partial</button>
            <button onClick={() => act("overpayment")} data-testid="zelle-action-overpayment" className="px-3 py-2 rounded-xl bg-violet-50 text-violet-700 dark:bg-violet-500/10 text-sm font-medium hover:opacity-80">Mark Overpayment</button>
            <button onClick={() => act("duplicate")} data-testid="zelle-action-duplicate" className="px-3 py-2 rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-500/10 text-sm font-medium hover:opacity-80">Duplicate</button>
            <button onClick={() => act("ignore")} data-testid="zelle-action-ignore" className="col-span-2 px-3 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted">Ignore Payment</button>
          </div>
        </div>
        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <button onClick={del} data-testid="zelle-dialog-delete" className="inline-flex items-center gap-1 text-sm text-destructive hover:opacity-80"><Trash2 className="w-4 h-4" /> Delete</button>
            <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /> Close</button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
